export interface FilterState {
  platforms: string[];
  price: "all" | "free" | "paid";
  size: "all" | "small" | "medium" | "large";
}

interface FilterPanelProps {
  subcategories: string[];
  activeSub: string;
  onSubChange: (sub: string) => void;
  filters: FilterState;
  onTogglePlatform: (p: string) => void;
  onPriceChange: (v: FilterState["price"]) => void;
  onSizeChange: (v: FilterState["size"]) => void;
  onReset: () => void;
}

const sizeOptions: { value: FilterState["size"]; label: string }[] = [
  { value: "all", label: "不限大小" },
  { value: "small", label: "100 MB 以下" },
  { value: "medium", label: "100 MB - 500 MB" },
  { value: "large", label: "500 MB 以上" },
];

const priceOptions: { value: FilterState["price"]; label: string }[] = [
  { value: "all", label: "全部" },
  { value: "free", label: "免费" },
  { value: "paid", label: "付费" },
];

export default function FilterPanel({
  subcategories,
  activeSub,
  onSubChange,
  filters,
  onTogglePlatform,
  onPriceChange,
  onSizeChange,
  onReset,
}: FilterPanelProps) {
  const blockTitle = "text-xs font-semibold text-foreground-800 mb-2.5";

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h3 className="font-heading text-sm font-bold text-foreground-950 flex items-center gap-1.5">
          <span className="flex items-center justify-center w-4 h-4 text-primary-600"><i className="ri-filter-3-line" aria-hidden="true"></i></span>
          筛选条件
        </h3>
        <button
          type="button"
          onClick={onReset}
          className="text-xs text-secondary-500 hover:text-primary-600 transition-colors cursor-pointer whitespace-nowrap flex items-center gap-1"
        >
          <span className="flex items-center justify-center w-3.5 h-3.5"><i className="ri-refresh-line" aria-hidden="true"></i></span>
          重置
        </button>
      </div>

      <div>
        <p className={blockTitle}>子分类</p>
        <div className="flex flex-wrap gap-1.5">
          <button
            type="button"
            onClick={() => onSubChange("all")}
            className={`text-xs rounded-full px-2.5 py-1 transition-colors cursor-pointer whitespace-nowrap ${
              activeSub === "all" ? "bg-primary-500 text-background-50" : "bg-secondary-100 text-secondary-600 hover:bg-secondary-200"
            }`}
          >
            全部
          </button>
          {subcategories.map((sub) => (
            <button
              key={sub}
              type="button"
              onClick={() => onSubChange(sub)}
              className={`text-xs rounded-full px-2.5 py-1 transition-colors cursor-pointer whitespace-nowrap ${
                activeSub === sub ? "bg-primary-500 text-background-50" : "bg-secondary-100 text-secondary-600 hover:bg-secondary-200"
              }`}
            >
              {sub}
            </button>
          ))}
        </div>
      </div>

      <div>
        <p className={blockTitle}>支持平台</p>
        <div className="space-y-2">
          {[
            { value: "android", label: "安卓 (Android)", icon: "ri-android-line" },
            { value: "ios", label: "iOS", icon: "ri-apple-fill" },
          ].map((opt) => (
            <label key={opt.value} className="flex items-center gap-2 text-sm text-foreground-700 cursor-pointer">
              <input
                type="checkbox"
                checked={filters.platforms.includes(opt.value)}
                onChange={() => onTogglePlatform(opt.value)}
                className="w-4 h-4 rounded border-secondary-300 text-primary-500 focus:ring-primary-400 cursor-pointer accent-primary-500"
              />
              <span className="flex items-center justify-center w-4 h-4 text-secondary-500"><i className={opt.icon} aria-hidden="true"></i></span>
              {opt.label}
            </label>
          ))}
        </div>
      </div>

      <div>
        <p className={blockTitle}>收费类型</p>
        <div className="flex flex-wrap gap-1.5">
          {priceOptions.map((opt) => (
            <button
              key={opt.value}
              type="button"
              onClick={() => onPriceChange(opt.value)}
              className={`text-xs rounded-md px-3 py-1.5 transition-colors cursor-pointer whitespace-nowrap ${
                filters.price === opt.value ? "bg-primary-500 text-background-50" : "bg-secondary-100 text-secondary-600 hover:bg-secondary-200"
              }`}
            >
              {opt.label}
            </button>
          ))}
        </div>
      </div>

      <div>
        <p className={blockTitle}>软件大小</p>
        <div className="space-y-2">
          {sizeOptions.map((opt) => (
            <label key={opt.value} className="flex items-center gap-2 text-sm text-foreground-700 cursor-pointer">
              <input
                type="radio"
                name="size"
                checked={filters.size === opt.value}
                onChange={() => onSizeChange(opt.value)}
                className="w-4 h-4 border-secondary-300 text-primary-500 focus:ring-primary-400 cursor-pointer accent-primary-500"
              />
              {opt.label}
            </label>
          ))}
        </div>
      </div>
    </div>
  );
}